// Global Instructions Rule Applied!
//
// "Earlier years" section of the Closing fund transaction form
// (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md section 5 and 4.2 "As built",
// PR 3): informational. For a fund on the Financial year basis it shows
// what the partners the closing admits will take extra of (and the others
// less) per K-1 tax category over the coming years - the engine's
// carry-forward at the closing date for the open year, with what the year's
// items to that date already cure. One read (view__fund_closing_earlier_years
// through GET /fund-transactions/:id/earlier-years); a Draft closing is
// previewed with its selection applied on the server, nothing written. A
// fund on Static has nothing to show, and the section renders nothing.
//
// The closing itself books nothing of this: the year close books the
// allocation. Section-local state, no redux slice; the parent re-keys the
// read on Save / Post / Unbook.

import React, { useEffect, useMemo, useState } from 'react'
import { Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import NumberText from '../../../core/components/NumberText.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundClosingEarlierYears } from '../services/fundTransactionsService.ts'
import type {
  ClosingEarlierYearsRow,
  FundClosingEarlierYears
} from '../../../state_manager/data_models/fund_closing_earlier_years.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const pct = (v: number): string => `${(v * 100).toFixed(2)}%`
const money = (v: number | null | undefined): React.ReactNode => <NumberText value={v} />

interface ClosingEarlierYearsSectionProps {
  /** the saved closing; null while the record is new */
  transactionId: string | null
  /** changes when the closing is saved, posted or unbooked: the read re-runs */
  refreshKey: string
}

export default function ClosingEarlierYearsSection({ transactionId, refreshKey }: ClosingEarlierYearsSectionProps) {
  const [data, setData] = useState<FundClosingEarlierYears | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!transactionId) {
      setData(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    void fetchFundClosingEarlierYears(transactionId, token).then((res) => {
      if (cancelled) return
      setLoading(false)
      setError(res.error ?? null)
      setData(res.data)
    })
    return () => {
      cancelled = true
    }
  }, [transactionId, refreshKey])

  const rows: ClosingEarlierYearsRow[] = useMemo(() => data?.rows ?? [], [data])
  const net = useMemo(() => rows.reduce((sum, r) => sum + r.prog__carry_forward, 0), [rows])

  // nothing to show: a new record, a fund on Static, or nothing loaded yet
  if (!transactionId) return null
  if (!data && !error) return null
  if (data && !data.per_fin_year) return null

  const columns = [
    {
      title: 'Partner',
      dataIndex: 'investors__name',
      render: (v: string, row: ClosingEarlierYearsRow) => (
        <span>
          {v}
          {row.prog__is_new ? (
            <Tag color='purple' className='ml-2'>
              New
            </Tag>
          ) : null}
        </span>
      )
    },
    { title: 'Share', dataIndex: 'prog__share', width: 90, align: 'right' as const, render: (v: number) => pct(v) },
    { title: 'Tax category', dataIndex: 'tax_categories__label', width: 220 },
    { title: 'Catch-up wanted', dataIndex: 'prog__want', width: 140, align: 'right' as const, render: money },
    { title: 'Cured this year to date', dataIndex: 'prog__curative', width: 170, align: 'right' as const, render: money },
    { title: 'Carried to later years', dataIndex: 'prog__carry_forward', width: 170, align: 'right' as const, render: money }
  ]

  return (
    <FormSection
      title='Earlier years'
      titleColor='purple'
      scrollable={false}
      loading={loading}
      headerActions={
        <>
          {data ? data.is_booked ? <Tag color='green'>Booked</Tag> : <Tag color='blue'>Preview</Tag> : null}
          {data?.fin_year ? (
            <Text type='secondary'>
              FY {data.fin_year.fin_year}
              {data.closing_date ? ` · closing ${dayjs(data.closing_date).format(DATE_FORMAT)}` : ''}
            </Text>
          ) : null}
        </>
      }
    >
      {error ? <Text type='danger'>{error}</Text> : null}
      {data?.status_message ? (
        <Text type='secondary' className='block mb-2'>
          {data.status_message}
        </Text>
      ) : null}
      {rows.length > 0 ? (
        <>
          <Table
            size='small'
            columns={columns}
            dataSource={rows}
            pagination={false}
            rowKey={(r) => `${r.investors__id}|${r.tax_categories__id ?? 'unmapped'}`}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row className='font-medium'>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    Net
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align='right'>
                    <NumberText value={net} wrap={(f) => <Text strong>{f}</Text>} />
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <Text type='secondary' className='block mt-2'>
            A positive carry-forward is what the partner still takes extra of the category&apos;s future items; a
            negative one is what it gives back. The amounts are the engine&apos;s at the closing date; nothing is
            booked by the closing itself - the year close books the allocation.
          </Text>
        </>
      ) : null}
    </FormSection>
  )
}
